import { FC, useState, useEffect } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import {
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Paper,
  Typography,
  IconButton,
  Checkbox,
  FormControlLabel,
  TablePagination,
  Tooltip,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@material-ui/core';
import ControlPointIcon from '@material-ui/icons/ControlPoint';
import PreviewIcon from '@material-ui/icons/RemoveRedEyeSharp';
import { Add } from '@material-ui/icons';
import DeleteIcon from '@material-ui/icons/Delete';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import { blue, orange, red } from '@material-ui/core/colors';
import { FederationData, FederationFormData, defaultValue, FederationTableProps } from './types';
import axios from 'server';
// import MicrosoftSvg from '../../../assets/microsoft.png';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { useStore } from 'hooks/useRootStore';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ContentCopyIcon from '@material-ui/icons/FileCopy';
import CheckIcon from '@material-ui/icons/Check';
// import CloseIcon from '@material-ui/icons/Close';
const FederationTable: FC<FederationTableProps> = ({ federationKey }) => {
  console.log('federationKey', federationKey);

  const [open, setOpen] = useState(false);
  const [view, setView] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedFederation, setSelectedFederation] = useState<FederationData | null>(null);
  const [selectedForAdd, setSelectedForAdd] = useState<FederationData | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [perPage, setPerPage] = useState(25);
  const [detailPage, setDetailPage] = useState(1);
  const [detailPerPage, setDetailPerPage] = useState(25);
  const [detailTotalCount, setDetailTotalCount] = useState(0);
  const [selectedSource, setSelectedSource] = useState('');

  const isAlreadyAdded = (federation: FederationData) =>
    Boolean(
      federation.owner_client_id ||
        federation.is_registered ||
        (federation.entity_id &&
          federationLists.some(
            (item) =>
              item.id !== federation.id &&
              item.entity_id === federation.entity_id &&
              Boolean(item.owner_client_id || item.is_registered)
          ))
    );

  const formatDate = (value?: string | null) => {
    if (!value) return 'Not available';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Not available';
    return date.toLocaleString();
  };

  const metadataStatus = (value?: string | null) => {
    if (!value) return { label: 'Action needed', color: 'secondary' as const };
    const date = new Date(value);
    if (Number.isNaN(date.getTime()) || date.getTime() <= Date.now()) {
      return { label: 'Action needed', color: 'secondary' as const };
    }
    return { label: 'Valid', color: 'primary' as const };
  };

  const handleCopy = async (value: string, key: string) => {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    setCopiedField(key);
    setTimeout(() => setCopiedField(null), 1500);
  };

  const handleAddReview = (federation: FederationData) => {
    setSelectedForAdd(federation);
  };

  const handleConfirmAdd = () => {
    if (!selectedForAdd) return;

    // Keep the original Add-to-EasyIdentity API flow.
    // The review screen should only be a confirmation step; it must not block
    // the mutation based on client-side metadata/duplicate checks.
    toggleActive.mutate(selectedForAdd.id, {
      onSuccess: () => {
        setSelectedForAdd(null);
        refetch();
      },
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setDetailPage(1);
      setSearchQuery(searchInput);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<FederationFormData>({ defaultValues: defaultValue });

  const { fields: acsFields, append: appendAcs, remove: removeAcs } = useFieldArray({ control, name: 'acs_endpoints' });
  const { fields: sloFields, append: appendSlo, remove: removeSlo } = useFieldArray({ control, name: 'slo_endpoints' });
  const {
    notificationStore: { openNotification },
  } = useStore();

  const {
    data: federationList = [],
    isLoading: isFetchingFederation,
    isError: isFederationError,
    refetch,
  } = useQuery<FederationData[]>(
    ['app-federation-configure-files', page, perPage, searchQuery],
    async () => {
      const params = new URLSearchParams({
        page: String(page),
        per_page: String(perPage),
      });

      if (searchQuery) {
        params.set('search', searchQuery);
      }
      // const endpoint =
      //   federationKey === 'service'
      //     ? 'saml/service-providers/defaults/files'
      //     : 'saml/identity-providers/defaults/files';

      // const response = await axios.get(
      //   `${endpoint}?${params.toString()}`
      // );
      const response = await axios.get(`saml/service-providers/defaults/files?${params.toString()}`);

      const raw = response.data;

      setTotalCount(raw.total ?? 0);

      return Array.isArray(raw.data) ? raw.data : [];
    },
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  const {
    data: federationLists = [],
    isLoading: isFetchingFederations,
    // isError: isFederationErrors,
  } = useQuery<FederationData[]>(
    ['app-federation-configure-edugain', detailPage, detailPerPage, selectedSource, searchQuery],
    async () => {
      const endpoint =
        federationKey === 'service' ? 'saml/service-providers/defaults' : 'saml/identity-providers/defaults';

      const params = new URLSearchParams({
        source_file: selectedSource,
        page: String(detailPage),
        per_page: String(detailPerPage),
      });
      if (searchQuery) params.set('search', searchQuery);

      const response = await axios.get(`${endpoint}?${params.toString()}`);
      const raw = response.data;
      setDetailTotalCount(raw.total ?? 0);
      return Array.isArray(raw.data) ? raw.data : [];
    },
    {
      enabled: !!selectedSource && view,
      refetchOnWindowFocus: false,
      keepPreviousData: false,
    }
  );
  const createFederation = useMutation(
    (data: FederationFormData) => axios.post('saml/service-providers/defaults', data),
    {
      onSuccess: () => {
        openNotification('success', 'Federation updated successfully');
      },
      onError: (error: any) => {
        const message = error?.response?.data?.message || error?.response?.data?.error || 'Something went wrong';
        openNotification('error', message);
      },
    }
  );

  const deleteFederation = useMutation((id: number) => axios.delete(`saml/service-providers/defaults/${id}`), {
    onSuccess: () => {
      openNotification('success', 'Federation updated successfully');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.response?.data?.error || 'Something went wrong';
      openNotification('error', message);
    },
  });

  const toggleActive = useMutation(
    (id: number) => {
      const endpoint =
        federationKey === 'service'
          ? `saml/service-providers/from-default/${id}`
          : `saml/identity-providers/from-default/${id}`;

      return axios.post(endpoint, { is_active: true });
    },
    // (id: number) => axios.post(`saml/service-providers/from-default/${id}`, { is_active: true }),
    {
      onSuccess: () => {
        openNotification('success', 'Federation configuration updated successfully');
      },
      onError: (error: any) => {
        const message = error?.response?.data?.message || error?.response?.data?.error || 'Something went wrong';
        openNotification('error', message);
      },
    }
  );

  // const getProviderIcon = (name?: string) => {
  //   const provider = (name || '').toLowerCase();
  //   if (provider.includes('microsoft')) {
  //     return MicrosoftSvg;
  //   }
  //   return '';
  // };

  const onSubmit = (data) => {
    const payload = {
      ...data,
      certificates: [
        {
          use: 'signing',
          certificate: data.certificates?.[0]?.certificate || '',
        },
        {
          use: 'encryption',
          certificate: data.certificates?.[1]?.certificate || '',
        },
      ],
    };

    createFederation.mutate(payload, {
      onSuccess: (response) => {
        const responseData = response?.data?.data || response?.data;
        if (responseData) {
          setSelectedFederation(responseData);
          setOpen(false);
          setViewOpen(true);
        }
        reset(defaultValue);
        refetch();
      },
    });
  };

  const handleClose = () => {
    setOpen(false);
    reset(defaultValue);
    createFederation.reset();
  };

  const handleDeleteOpen = (federation: FederationData) => {
    setSelectedFederation(federation);
    setDeleteOpen(true);
  };
  const handleDeleteClose = () => {
    setDeleteOpen(false);
    setSelectedFederation(null);
    deleteFederation.reset();
  };
  const handleDeleteConfirm = () => {
    if (!selectedFederation) return;
    deleteFederation.mutate(selectedFederation.id, {
      onSuccess: () => {
        setDeleteOpen(false);
        setSelectedFederation(null);
        refetch();
      },
    });
  };

  const handleView = (federation: FederationData) => {
    setSelectedFederation(federation);
    setViewOpen(true);
  };

  const handleViewClose = () => {
    setViewOpen(false);
    setSelectedFederation(null);
  };
  useAppPreloader(
    isFetchingFederation ||
      isFetchingFederations ||
      createFederation.isLoading ||
      deleteFederation.isLoading ||
      toggleActive.isLoading
  );
  return (
    <>
      {!view && (
        <Grid container alignItems='center' style={{ marginBottom: 16 }}>
          <Grid item xs>
            <TextField
              placeholder='Search federations...'
              variant='outlined'
              size='small'
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{ width: 280 }}
            />
          </Grid>
          <Grid item>
            <Button
              variant='contained'
              color='secondary'
              style={{ backgroundColor: '#5081FF' }}
              onClick={() => setOpen(true)}
            >
              <ControlPointIcon />
            </Button>
          </Grid>
        </Grid>
      )}
      {isFetchingFederation ? (
        <></>
      ) : isFederationError ? (
        <Typography
          color='error'
          style={{
            padding: 20,
          }}
        >
          Failed to load federation applications.
        </Typography>
      ) : federationList.length === 0 ? (
        <Typography style={{ padding: 20, textAlign: 'center' }}>No Federation Applications found.</Typography>
      ) : view === false ? (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Source File</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Source Type</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Source Url</strong>
                  </TableCell>
                  <TableCell align='right'>
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {federationList.map((federation) => (
                  <TableRow key={federation.id} hover>
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Typography style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>
                          {federation.source_file || '-'}
                        </Typography>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Typography
                        style={{ color: '#6b7280', fontSize: 13, wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                      >
                        {federation.source_type || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        style={{ color: '#6b7280', fontSize: 13, wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                      >
                        {federation.source_url || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align='right'>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4 }}>
                        <IconButton
                          size='small'
                          onClick={() => {
                            setSelectedSource(federation.source_file);
                            setDetailPage(1);
                            setDetailPerPage(25);
                            setView(true);
                          }}
                        >
                          <PreviewIcon style={{ color: orange[500] }} />
                        </IconButton>
                        {federation.owner_client_id && (
                          <IconButton size='small' onClick={() => handleDeleteOpen(federation)}>
                            <DeleteIcon style={{ color: red[500] }} />
                          </IconButton>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {totalCount > 0 && (
            <TablePagination
              component='div'
              count={totalCount}
              page={page - 1}
              onChangePage={(_: any, newPage: number) => setPage(newPage + 1)}
              rowsPerPage={perPage}
              rowsPerPageOptions={[25, 50, 100]}
              onChangeRowsPerPage={(event: React.ChangeEvent<HTMLInputElement>) => {
                const newPerPage = parseInt(event.target.value, 10);
                setPerPage(newPerPage);
                setPage(1);
              }}
              style={{ marginTop: 8 }}
            />
          )}{' '}
        </>
      ) : (
        <>
          {selectedForAdd ? (
            /* Step 4 is a separate screen. It is shown only after clicking
             "Add to EasyIdentity" from the Step 3 catalog table. */
            <Grid container justify='center' style={{ background: '#f4f7fb', margin: 0, padding: '8px 0 20px' }}>
              <Grid item xs={12} md={8} lg={7}>
                <Paper
                  elevation={2}
                  style={{
                    borderRadius: 10,
                    overflow: 'hidden',
                    boxShadow: '0 3px 12px rgba(15, 23, 42, 0.10)',
                  }}
                >
                  <div
                    style={{
                      background: '#4f7df3',
                      color: '#fff',
                      padding: '10px 14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    {/* <span
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      background: '#fff',
                      color: '#4f7df3',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    4
                  </span> */}
                    <Typography style={{ color: '#fff', fontWeight: 700, fontSize: 18 }}>
                      Review a federation SP
                    </Typography>
                  </div>

                  <div style={{ padding: '16px 18px 18px' }}>
                    <Button
                      size='small'
                      startIcon={<ArrowBackIcon />}
                      onClick={() => setSelectedForAdd(null)}
                      style={{ textTransform: 'none', marginBottom: 8, paddingLeft: 0 }}
                    >
                      Back to federation catalog
                    </Button>

                    <Typography style={{ fontSize: 18, fontWeight: 700, color: '#273142' }}>
                      {selectedForAdd.name || 'Service Provider'}
                    </Typography>
                    <Typography variant='body2' style={{ color: '#718096', marginTop: 3 }}>
                      From {selectedSource || 'federation'} ·{' '}
                      {isAlreadyAdded(selectedForAdd) ? 'Added to EasyIdentity' : 'Available to add'}
                    </Typography>

                    {(() => {
                      const status = metadataStatus(selectedForAdd.metadata_valid_until);
                      const valid = status.label === 'Valid';
                      return (
                        <div
                          style={{
                            marginTop: 16,
                            padding: '10px 14px',
                            borderRadius: 6,
                            background: valid ? '#e5f7ed' : '#fff4e5',
                          }}
                        >
                          <Typography
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: valid ? '#2b9a68' : '#b76b00',
                            }}
                          >
                            Metadata status: {valid ? 'valid' : 'action needed'}
                          </Typography>
                          <Typography variant='body2' style={{ fontSize: 11, color: '#718096', marginTop: 2 }}>
                            {valid
                              ? `Show expiry and last refresh from verified source data. Expires ${formatDate(
                                  selectedForAdd.metadata_valid_until
                                )}.`
                              : 'Metadata is expired or cannot be verified. Refresh or verify the source data.'}
                          </Typography>
                        </div>
                      );
                    })()}

                    <div style={{ marginTop: 14 }}>
                      {[
                        ['Entity ID', selectedForAdd.entity_id || '-'],
                        [
                          'Sign-in endpoint',
                          selectedForAdd.acs_url || 'Shown if supported and selected by EasyIdentity',
                        ],
                        ['Source', `${selectedSource || 'Federation'} federation metadata`],
                      ].map(([label, value]) => (
                        <div
                          key={label}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '145px minmax(0, 1fr)',
                            gap: 10,
                            padding: '11px 0',
                            borderBottom: '1px solid #edf0f4',
                          }}
                        >
                          <Typography style={{ fontSize: 12, fontWeight: 700, color: '#273142' }}>{label}</Typography>
                          <Typography
                            style={{
                              fontSize: 12,
                              color: '#718096',
                              wordBreak: 'break-all',
                            }}
                          >
                            {value}
                          </Typography>
                        </div>
                      ))}
                    </div>

                    <Accordion
                      elevation={0}
                      style={{
                        marginTop: 12,
                        background: '#f4f7fb',
                        borderRadius: 6,
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ minHeight: 48 }}>
                        <Typography style={{ fontSize: 12, color: '#374151' }}>
                          Technical metadata: endpoints, certificates
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <div style={{ width: '100%' }}>
                          <Typography variant='body2' style={{ fontSize: 12, marginBottom: 6 }}>
                            <strong>ACS URL:</strong> {selectedForAdd.acs_url || 'Not available'}
                          </Typography>
                          <Typography variant='body2' style={{ fontSize: 12, marginBottom: 6 }}>
                            <strong>Metadata valid until:</strong> {formatDate(selectedForAdd.metadata_valid_until)}
                          </Typography>
                          <Typography variant='body2' style={{ fontSize: 12 }}>
                            <strong>Imported:</strong> {formatDate(selectedForAdd.imported_at)}
                          </Typography>
                        </div>
                      </AccordionDetails>
                    </Accordion>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                      <Button
                        variant='contained'
                        color='primary'
                        disabled={toggleActive.isLoading}
                        onClick={handleConfirmAdd}
                        style={{
                          textTransform: 'none',
                          minWidth: 156,
                          background: '#4f7df3',
                        }}
                      >
                        {toggleActive.isLoading ? <CircularProgress size={20} /> : 'Add to EasyIdentity'}
                      </Button>
                    </div>
                  </div>
                </Paper>
              </Grid>
            </Grid>
          ) : (
            <Grid container style={{ background: '#f4f7fb', margin: 0, padding: '8px 0 20px' }}>
              <Grid item xs={12}>
                <Paper
                  elevation={2}
                  style={{
                    borderRadius: 10,
                    overflow: 'hidden',
                    boxShadow: '0 3px 12px rgba(15, 23, 42, 0.10)',
                  }}
                >
                  <div style={{ padding: '16px 18px 14px' }}>
                    <Typography variant='body2' style={{ color: '#718096', marginBottom: 14 }}>
                      Federations / {selectedSource || 'Federation'} / Available service providers
                    </Typography>
                    <TextField
                      placeholder='Search applications by name or Entity ID'
                      variant='outlined'
                      size='small'
                      fullWidth
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      InputProps={{
                        style: { background: '#f8fafc', fontSize: 13 },
                      }}
                    />

                    <TableContainer style={{ marginTop: 14 }}>
                      <Table size='small'>
                        <TableHead>
                          <TableRow>
                            <TableCell style={{ fontWeight: 700, fontSize: 12, width: 470 }}>
                              Available service providers{' '}
                            </TableCell>
                            <TableCell style={{ fontWeight: 700, fontSize: 12, width: 210 }}>Status</TableCell>
                            <TableCell align='right' style={{ fontWeight: 700, fontSize: 12, width: 250 }}>
                              Action
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {federationLists.map((federation) => {
                            const alreadyAdded = isAlreadyAdded(federation);
                            return (
                              <TableRow key={federation.id} hover>
                                <TableCell style={{ padding: '11px 12px' }}>
                                  <Typography
                                    style={{
                                      fontWeight: 700,
                                      fontSize: 14,
                                      color: '#273142',
                                    }}
                                  >
                                    {federation.name || '-'}
                                  </Typography>
                                  <Typography
                                    variant='caption'
                                    style={{
                                      display: 'block',
                                      color: '#8a94a6',
                                      fontSize: 10,
                                      wordBreak: 'break-all',
                                    }}
                                  >
                                    {federation.entity_id || '-'}
                                  </Typography>
                                </TableCell>

                                <TableCell style={{ padding: '11px 12px' }}>
                                  <span
                                    style={{
                                      display: 'inline-block',
                                      minWidth: 68,
                                      textAlign: 'center',
                                      padding: '4px 8px',
                                      borderRadius: 14,
                                      background: alreadyAdded ? '#dff5e9' : '#edf1f7',
                                      color: alreadyAdded ? '#2b9a68' : '#758197',
                                      fontSize: 10,
                                      fontWeight: 700,
                                    }}
                                  >
                                    {alreadyAdded ? 'Added' : 'Available'}
                                  </span>
                                </TableCell>
                                <TableCell align='right' style={{ padding: '11px 12px' }}>
                                  <div
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'flex-end',
                                      alignItems: 'center',
                                      gap: 4,
                                    }}
                                  >
                                    <Tooltip title='Add to EasyIdentity' placement='top'>
                                      <IconButton
                                        size='small'
                                        disabled={toggleActive.isLoading}
                                        onClick={() => handleAddReview(federation)}
                                      >
                                        <Add style={{ color: blue[500] }} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title='View details' placement='top'>
                                      <IconButton size='small' onClick={() => handleView(federation)}>
                                        <PreviewIcon style={{ color: orange[500] }} />
                                      </IconButton>
                                    </Tooltip>
                                    {federation.owner_client_id && (
                                      <Tooltip title='Remove from EasyIdentity' placement='top'>
                                        <IconButton size='small' onClick={() => handleDeleteOpen(federation)}>
                                          <DeleteIcon style={{ color: red[500] }} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </div>
                                </TableCell>
                                {/* <TableCell align='right' style={{ padding: '11px 12px' }}>
                                {alreadyAdded ? (
                                  <Button
                                    size='small'
                                    color='primary'
                                    onClick={() => handleView(federation)}
                                    style={{ textTransform: 'none', fontSize: 11 }}
                                  >
                                    View details
                                  </Button>
                                ) : (
                                  <Button
                                    size='small'
                                    variant='contained'
                                    color='primary'
                                    disabled={toggleActive.isLoading}
                                    onClick={() => handleAddReview(federation)}
                                    style={{
                                      textTransform: 'none',
                                      fontSize: 10,
                                      padding: '7px 10px',
                                      whiteSpace: 'nowrap',
                                      background: '#4f7df3',
                                    }}
                                  >
                                    Add to EasyIdentity
                                  </Button>
                                )}
                              </TableCell> */}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {detailTotalCount > 0 && (
                      <TablePagination
                        component='div'
                        count={detailTotalCount}
                        page={detailPage - 1}
                        onChangePage={(_: any, newPage: number) => setDetailPage(newPage + 1)}
                        rowsPerPage={detailPerPage}
                        rowsPerPageOptions={[25, 50, 100]}
                        onChangeRowsPerPage={(event: React.ChangeEvent<HTMLInputElement>) => {
                          const newPerPage = parseInt(event.target.value, 10);
                          setDetailPerPage(newPerPage);
                          setDetailPage(1);
                        }}
                      />
                    )}
                  </div>
                </Paper>
              </Grid>
            </Grid>
          )}
        </>
      )}

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth='md'>
        <DialogTitle>Add Federation</DialogTitle>

        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Grid container spacing={2}>
              {/* Name */}
              <Grid item xs={12}>
                <TextField
                  label='Name'
                  fullWidth
                  variant='outlined'
                  {...register('name', { required: 'Name is required' })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              </Grid>

              {/* Entity ID */}
              <Grid item xs={12}>
                <TextField
                  label='Entity ID'
                  fullWidth
                  variant='outlined'
                  {...register('entity_id', { required: 'Entity ID is required' })}
                  error={!!errors.entity_id}
                  helperText={errors.entity_id?.message}
                />
              </Grid>

              {/* Source URL */}
              <Grid item xs={12}>
                <TextField
                  label='Source URL'
                  fullWidth
                  variant='outlined'
                  {...register('source_url', { required: 'Source URL is required' })}
                  error={!!errors.source_url}
                  helperText={errors.source_url?.message}
                />
              </Grid>

              {/* Metadata Valid Until */}
              <Grid item xs={12}>
                <TextField
                  label='Metadata Valid Until'
                  type='datetime-local'
                  fullWidth
                  variant='outlined'
                  InputLabelProps={{ shrink: true }}
                  {...register('metadata_valid_until', {
                    required: 'Metadata Valid Until is required',
                  })}
                  error={!!errors.metadata_valid_until}
                  helperText={errors.metadata_valid_until?.message}
                />
              </Grid>

              {/* ACS Endpoints */}
              <Grid item xs={12}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant='subtitle1'>ACS Endpoints</Typography>
                  <Button
                    size='small'
                    onClick={() => appendAcs({ Binding: '', Location: '', index: acsFields.length, isDefault: false })}
                  >
                    + Add
                  </Button>
                </div>
              </Grid>

              {acsFields.map((field, i) => (
                <Grid item xs={12} key={field.id}>
                  <Paper elevation={0} style={{ padding: 12, border: '1px solid #e0e0e0', borderRadius: 8 }}>
                    <Grid container spacing={2} alignItems='center'>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label='Binding'
                          fullWidth
                          variant='outlined'
                          {...register(`acs_endpoints.${i}.Binding`, { required: 'Binding is required' })}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label='Location'
                          fullWidth
                          variant='outlined'
                          {...register(`acs_endpoints.${i}.Location`, { required: 'Location is required' })}
                        />
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <TextField
                          label='Index'
                          type='number'
                          fullWidth
                          variant='outlined'
                          {...register(`acs_endpoints.${i}.index`, { valueAsNumber: true })}
                        />
                      </Grid>
                      <Grid item xs={4} md={1}>
                        <Controller
                          name={`acs_endpoints.${i}.isDefault`}
                          control={control}
                          render={({ field: f }) => (
                            <FormControlLabel
                              control={<Checkbox checked={f.value} onChange={(e) => f.onChange(e.target.checked)} />}
                              label='Default'
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={2} md={1}>
                        {acsFields.length > 1 && (
                          <IconButton size='small' onClick={() => removeAcs(i)}>
                            <DeleteIcon style={{ color: red[400] }} />
                          </IconButton>
                        )}
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              ))}

              {/* SLO Endpoints */}
              <Grid item xs={12}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant='subtitle1'>SLO Endpoints</Typography>
                  <Button size='small' onClick={() => appendSlo({ Binding: '', Location: '' })}>
                    + Add
                  </Button>
                </div>
              </Grid>

              {sloFields.map((field, i) => (
                <Grid item xs={12} key={field.id}>
                  <Paper elevation={0} style={{ padding: 12, border: '1px solid #e0e0e0', borderRadius: 8 }}>
                    <Grid container spacing={2} alignItems='center'>
                      <Grid item xs={12} md={5}>
                        <TextField
                          label='Binding'
                          fullWidth
                          variant='outlined'
                          {...register(`slo_endpoints.${i}.Binding`, { required: 'Binding is required' })}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label='Location'
                          fullWidth
                          variant='outlined'
                          {...register(`slo_endpoints.${i}.Location`, { required: 'Location is required' })}
                        />
                      </Grid>
                      <Grid item xs={12} md={1}>
                        {sloFields.length > 1 && (
                          <IconButton size='small' onClick={() => removeSlo(i)}>
                            <DeleteIcon style={{ color: red[400] }} />
                          </IconButton>
                        )}
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              ))}

              {/* Name ID Formats */}
              <Grid item xs={12}>
                <TextField
                  label='Name ID Format'
                  fullWidth
                  variant='outlined'
                  {...register('name_id_formats.0', {
                    required: 'Name ID Format is required',
                  })}
                />
              </Grid>

              {/* Signing Certificate */}
              <Grid item xs={12}>
                <Typography variant='subtitle1' gutterBottom>
                  Signing Certificate
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label='Certificate'
                  multiline
                  rows='4'
                  variant='outlined'
                  fullWidth
                  {...register('certificates.0.certificate', {
                    required: 'Signing certificate is required',
                  })}
                />
              </Grid>

              {/* Encryption Certificate */}
              <Grid item xs={12}>
                <Typography variant='subtitle1' gutterBottom>
                  Encryption Certificate
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label='Certificate'
                  multiline
                  rows={4}
                  variant='outlined'
                  fullWidth
                  {...register('certificates.1.certificate', {
                    required: 'Encryption certificate is required',
                  })}
                />
              </Grid>

              {/* Signed Requests */}
              <Grid item xs={12} md={6}>
                <Controller
                  name='authn_requests_signed'
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                      label='Authn Requests Signed'
                    />
                  )}
                />
              </Grid>

              {/* Signed Assertions */}
              <Grid item xs={12} md={6}>
                <Controller
                  name='want_assertions_signed'
                  control={control}
                  defaultValue={true}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                      label='Want Assertions Signed'
                    />
                  )}
                />
              </Grid>
            </Grid>

            {createFederation.isError && (
              <Typography
                style={{
                  color: 'red',
                  marginTop: 10,
                }}
              >
                {(createFederation.error as any)?.response?.data?.message || 'Something went wrong'}
              </Typography>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose} disabled={createFederation.isLoading}>
              Cancel
            </Button>

            <Button type='submit' variant='contained' color='primary' disabled={createFederation.isLoading}>
              {createFederation.isLoading ? <CircularProgress size={20} /> : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={viewOpen} onClose={handleViewClose} fullWidth maxWidth='md'>
        <DialogTitle
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '18px 24px',
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant='h6'
              style={{
                fontWeight: 700,
                lineHeight: 1.4,
              }}
            >
              Federation Service Provider Details
            </Typography>

            <Typography
              variant='body2'
              color='textSecondary'
              style={{
                marginTop: 4,
                lineHeight: 1.4,
              }}
            >
              Review identity, metadata health, and technical configuration
            </Typography>
          </div>
        </DialogTitle>

        <DialogContent style={{ padding: 24 }}>
          {selectedFederation && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper
                  elevation={1}
                  style={{
                    padding: 20,
                    borderRadius: 8,
                  }}
                >
                  <Typography variant='h6' style={{ marginBottom: 16, fontWeight: 700 }}>
                    Service Provider
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label='Name'
                        fullWidth
                        variant='outlined'
                        value={selectedFederation.name || ''}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label='Entity ID'
                        fullWidth
                        variant='outlined'
                        value={selectedFederation.entity_id || ''}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>

                    {selectedFederation.acs_url && (
                      <Grid item xs={12}>
                        <TextField
                          label='EasyIdentity ACS URL'
                          fullWidth
                          variant='outlined'
                          value={selectedFederation.acs_url}
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <Tooltip title={copiedField === 'acs_url' ? 'Copied' : 'Copy'}>
                                <IconButton
                                  size='small'
                                  onClick={() => handleCopy(selectedFederation.acs_url || '', 'acs_url')}
                                >
                                  {copiedField === 'acs_url' ? <CheckIcon /> : <ContentCopyIcon />}
                                </IconButton>
                              </Tooltip>
                            ),
                          }}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label='Status'
                        fullWidth
                        variant='outlined'
                        value={selectedFederation.is_active ? 'Active' : 'Inactive'}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label='Registered'
                        fullWidth
                        variant='outlined'
                        value={selectedFederation.is_registered ? 'Yes' : 'No'}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper
                  elevation={1}
                  style={{
                    padding: 20,
                    borderRadius: 8,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 16,
                      marginBottom: 16,
                    }}
                  >
                    <div>
                      <Typography variant='h6' style={{ fontWeight: 700 }}>
                        Metadata health
                      </Typography>
                      <Typography variant='body2' color='textSecondary'>
                        Check validity and the latest metadata import/refresh information.
                      </Typography>
                    </div>

                    {(() => {
                      const status = metadataStatus(selectedFederation.metadata_valid_until);
                      return <Chip size='small' label={status.label} color={status.color} variant='outlined' />;
                    })()}
                  </div>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label='Metadata Source'
                        fullWidth
                        variant='outlined'
                        value={selectedFederation.source_url || 'Not available'}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label='Metadata Valid Until'
                        fullWidth
                        variant='outlined'
                        value={formatDate(selectedFederation.metadata_valid_until)}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label='Metadata Imported / Refreshed'
                        fullWidth
                        variant='outlined'
                        value={formatDate(selectedFederation.imported_at)}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Accordion elevation={1}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <div>
                      <Typography variant='h6' style={{ fontWeight: 700 }}>
                        Technical metadata
                      </Typography>
                      <Typography variant='body2' color='textSecondary'>
                        Endpoints, certificates, and technical source information
                      </Typography>
                    </div>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant='subtitle1' style={{ fontWeight: 700, marginBottom: 8 }}>
                          ACS Endpoints
                        </Typography>
                      </Grid>

                      {selectedFederation.acs_endpoints?.length ? (
                        selectedFederation.acs_endpoints.map((endpoint, index) => (
                          <Grid item xs={12} key={index}>
                            <Paper
                              elevation={0}
                              style={{
                                padding: 12,
                                border: '1px solid #e0e0e0',
                                borderRadius: 8,
                              }}
                            >
                              <Typography variant='body2'>
                                <strong>Index:</strong> {endpoint.index}
                              </Typography>
                              <Typography variant='body2' style={{ marginTop: 4 }}>
                                <strong>Binding:</strong> {endpoint.Binding}
                              </Typography>
                              <Typography
                                variant='body2'
                                style={{
                                  wordBreak: 'break-all',
                                  marginTop: 4,
                                }}
                              >
                                <strong>Location:</strong> {endpoint.Location}
                              </Typography>
                            </Paper>
                          </Grid>
                        ))
                      ) : (
                        <Grid item xs={12}>
                          <Typography variant='body2' color='textSecondary'>
                            No ACS endpoint metadata available.
                          </Typography>
                        </Grid>
                      )}

                      <Grid item xs={12} style={{ marginTop: 8 }}>
                        <Typography variant='subtitle1' style={{ fontWeight: 700, marginBottom: 8 }}>
                          SLO Endpoints
                        </Typography>
                      </Grid>

                      {selectedFederation.slo_endpoints?.length ? (
                        selectedFederation.slo_endpoints.map((endpoint, index) => (
                          <Grid item xs={12} key={index}>
                            <Paper
                              elevation={0}
                              style={{
                                padding: 12,
                                border: '1px solid #e0e0e0',
                                borderRadius: 8,
                              }}
                            >
                              <Typography variant='body2'>
                                <strong>Binding:</strong> {endpoint.Binding}
                              </Typography>
                              <Typography
                                variant='body2'
                                style={{
                                  wordBreak: 'break-all',
                                  marginTop: 4,
                                }}
                              >
                                <strong>Location:</strong> {endpoint.Location}
                              </Typography>
                            </Paper>
                          </Grid>
                        ))
                      ) : (
                        <Grid item xs={12}>
                          <Typography variant='body2' color='textSecondary'>
                            No SLO endpoint metadata available.
                          </Typography>
                        </Grid>
                      )}

                      <Grid item xs={12} style={{ marginTop: 8 }}>
                        <Typography variant='subtitle1' style={{ fontWeight: 700, marginBottom: 8 }}>
                          Signing Certificate
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label='Certificate'
                          fullWidth
                          multiline
                          rows={6}
                          variant='outlined'
                          value={selectedFederation.certificates?.[0]?.certificate || 'Not available'}
                          InputProps={{
                            readOnly: true,
                            endAdornment: selectedFederation.certificates?.[0]?.certificate ? (
                              <Tooltip title={copiedField === 'certificate' ? 'Copied' : 'Copy'}>
                                <IconButton
                                  size='small'
                                  onClick={() =>
                                    handleCopy(selectedFederation.certificates?.[0]?.certificate || '', 'certificate')
                                  }
                                >
                                  {copiedField === 'certificate' ? <CheckIcon /> : <ContentCopyIcon />}
                                </IconButton>
                              </Tooltip>
                            ) : undefined,
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          label='Certificate Use'
                          fullWidth
                          variant='outlined'
                          value={selectedFederation.certificates?.[0]?.use || 'Not available'}
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          label='Authn Requests Signed'
                          fullWidth
                          variant='outlined'
                          value={selectedFederation.authn_requests_signed ? 'Yes' : 'No'}
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          label='Want Assertions Signed'
                          fullWidth
                          variant='outlined'
                          value={selectedFederation.want_assertions_signed ? 'Yes' : 'No'}
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleViewClose} variant='contained' color='primary'>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={handleDeleteClose} maxWidth='xs' fullWidth>
        <DialogTitle>Delete Federation</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <b>{selectedFederation?.name}</b>?
          </Typography>
          {deleteFederation.isError && (
            <Typography style={{ color: 'red', marginTop: 8 }}>
              {(deleteFederation.error as any)?.response?.data?.message || 'Something went wrong'}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose} disabled={deleteFederation.isLoading}>
            Cancel
          </Button>
          <Button
            variant='contained'
            style={{ backgroundColor: red[500], color: '#fff' }}
            onClick={handleDeleteConfirm}
            disabled={deleteFederation.isLoading}
          >
            {deleteFederation.isLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FederationTable;
